// شريط سعر الدولار — **إعلان صغير يتغيّر يوميًا** (قرار المالك).
//
// لماذا في الترويسة لا في تبويب: البياع يحتاج سعر التحصيل وهو يكلّم العميل، لا بعد أن
// يتذكّر أن في النظام محوّل عملات. فالرقم أمامه دائمًا، ويتغيّر مع كل إعلان جديد من البنك
// بلا أن يلمس أحدٌ شيئًا.
//
// **وصغيرٌ في صفّ أدوات الترويسة** (قرار المالك): مع KPI والإعلان والجرس — يراه من يريده ولا يزاحم العمل. والتفصيل كلّه —
// أسعار البنك وتاريخها ولحظة آخر تحديث — في تبويب «محوّل العملات»، وهنا سطر واحد.
//
// ومصدره **نفس مسار المحوّل** (`/fx/rates`): لا مسار ثانٍ ولا حساب ثانٍ — السعران يأتيان
// محسوبين من الخادم، وهذا يعرض.
//
// ويختفي بلا ضجّة إن لم يوجد سعر بعد أو تعذّرت القراءة: شريطٌ فارغ أسوأ من لا شريط.
import { useEffect, useState } from 'react';
import { get, type FxReport } from '../lib/api.ts';
import { shortDate } from '../lib/format.ts';
import { Flag } from './flags.tsx';

export function FxTicker({ compact = false }: { /** بطاقاتٌ بسطرٍ واحد لترويسة سطح المكتب (04/10/2026) */ compact?: boolean } = {}) {
  const [data, setData] = useState<FxReport | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try { const r = await get<FxReport>('/fx/rates'); if (alive) setData(r); }
      catch { /* شريط معلومات: فشله لا يُقلق المستخدم ولا يملأ الشاشة بتنبيه */ }
    })();
    return () => { alive = false; };
  }, []);

  const usd = data?.rows.find(r => r.currencyCode === 'USD');
  // والريال بنفس البنية حرفيًا (قرار المالك) — يظهر إن كان له سعر، ولا يُعطّل الشريط إن غاب
  const sar = data?.rows.find(r => r.currencyCode === 'SAR');
  if (!data || !usd || data.rateDate === null || usd.ourRate === null) return null;

  const asOf = `أسعار البنك المركزي بتاريخ ${shortDate(data.rateDate)}`;

  return (
    // مكانه صفّ أدوات الترويسة (مع KPI والإعلان والجرس)، وكل عملة بطاقتها المستقلّة
    <div className="flex w-fit items-center gap-2">
      <Card flag="US" flagTitle="أمريكا" name="الدولار" collect={usd.ourRate} sell={usd.ourSell} compact={compact}
        title={`${asOf} — شراء ${usd.buy} · بيع ${usd.sell}`} />
      {sar?.ourRate && (
        <Card flag="SA" flagTitle="السعودية" name="الريال" collect={sar.ourRate} sell={sar.ourSell} compact={compact}
          title={`${asOf} — شراء ${sar.buy} · بيع ${sar.sell}`} />
      )}
      {data.ageDays !== null && data.ageDays > 1 && (
        <span className="rounded bg-amber-100 px-1 text-xs text-amber-900">{data.ageDays} يوم</span>
      )}
    </div>
  );
}

/**
 * بطاقة عملة **مستقلّة** (قرار المالك): علمها واسمها في المنتصف فوق، وتحتهما السعران —
 * كلمةٌ صغيرة ورقمها تحتها. بنيةٌ واحدة لكل عملة: ما يُطبَّق على الدولار يُطبَّق على الريال.
 */
/** رقمٌ مختصر للقراءة السريعة: رقمان بعد الفاصلة بلا تقريب يرفع القيمة (قرار المالك). */
const short = (v: string): string => {
  const m = /^(-?\d+)(?:\.(\d{0,2}))?/.exec(v.trim());
  return m ? (m[2] ? `${m[1]}.${m[2].padEnd(2, '0')}` : m[1]) : v;
};

function Card({ flag, flagTitle, name, collect, sell, title, compact = false }: {
  flag: 'US' | 'SA'; flagTitle: string; name: string; collect: string; sell: string | null; title: string; compact?: boolean;
}) {
  // **سطرٌ واحد في ترويسة سطح المكتب** (قرار المالك 04/10/2026: «الاطار ال فوق دا واكل من الشاشه كتير جدا»): البطاقة كانت
  // ثلاثة أسطر (الاسم، ثم كلمةٌ ورقمٌ تحتها) — نفس الكلمات والأرقام متجاورةً، فلا يرتفع الصفّ بسببها.
  if (compact) {
    return (
      <span title={title}
        className="flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md border border-emerald-200 bg-emerald-50/70 px-2 py-0.5 text-[11px] leading-tight text-emerald-900">
        <Flag code={flag} title={flagTitle} />
        <span className="font-semibold">{name}</span>
        <span className="opacity-80">تحصيل</span><strong className="tabular-nums">{short(collect)}</strong>
        {sell && (<><span className="opacity-80">· بيع</span><strong className="tabular-nums">{short(sell)}</strong></>)}
      </span>
    );
  }
  return (
    <span title={title}
      className="flex w-fit flex-col items-center gap-0.5 rounded-lg border border-emerald-200 bg-emerald-50/70 px-2.5 py-1 text-xs leading-tight text-emerald-900">
      <span className="flex items-center gap-1.5">
        <Flag code={flag} title={flagTitle} />
        <span className="font-semibold">{name}</span>
      </span>
      <span className="flex items-center gap-3">
        <span className="flex flex-col items-center">
          <span className="text-[0.9em] opacity-80">تحصيل</span>
          <strong className="tabular-nums">{short(collect)}</strong>
        </span>
        {sell && (
          <span className="flex flex-col items-center border-s border-emerald-200 ps-3">
            <span className="text-[0.9em] opacity-80">بيع</span>
            <strong className="tabular-nums">{short(sell)}</strong>
          </span>
        )}
      </span>
    </span>
  );
}
